import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  Clock,
  History as HistoryIcon,
  Building2,
  TrendingUp,
  Calendar,
} from 'lucide-react';
import { api, fmt } from '../../lib/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useMe } from '../../lib/useMe';

// ~30 ways to say hello — a different one greets the agent each time they
// land on the dashboard. Purely cosmetic.
const GREETINGS = [
  'Hello', 'Hi', 'Hey', 'Howdy', 'Hiya', 'Greetings', 'Salutations', 'Welcome back',
  'Yo', 'Ahoy', 'Aloha', 'Hola', 'Bonjour', 'Ciao', 'Hallo', 'Guten Tag',
  'Konnichiwa', 'Namaste', 'Ní hǎo', 'Annyeong', 'Olá', 'Hej', 'Privet', 'Shalom',
  'Salaam', 'Marhaba', 'Sawubona', "G'day", "What's up", 'Bonjourno',
];

export default function MyDashboard() {
  const { agentId, displayName, carryForward } = useMe();
  const navigate = useNavigate();
  // Pick once per mount so it stays stable while viewing, fresh on each visit.
  const [greeting] = useState(() => GREETINGS[Math.floor(Math.random() * GREETINGS.length)]);

  const rowsQuery = useQuery({
    queryKey: ['agent-rows', agentId],
    queryFn: () => api.agentRows(agentId),
    enabled: !!agentId,
  });

  const paymentsQuery = useQuery({
    queryKey: ['my-payments', agentId],
    queryFn: () => api.listPayments(undefined, agentId),
    enabled: !!agentId,
  });

  if (rowsQuery.isLoading || paymentsQuery.isLoading) {
    return <LoadingSpinner label="Loading your dashboard…" />;
  }

  const rows = rowsQuery.data?.rows ?? [];
  const payments = paymentsQuery.data?.payments ?? [];

  const lifetimePaid = payments.reduce((s, p) => s + (p.total || 0), 0);
  // Pending = the carried-forward balance store from prior held statements
  // (≤0). It's applied to the agent's next positive statement, then returns to
  // $0. Open/unfinalized cycle rows are intentionally NOT shown here.
  const pending = carryForward;
  const thisYearPaid = payments
    .filter((p) => new Date(p.paid_at).getFullYear() === new Date().getFullYear())
    .reduce((s, p) => s + (p.total || 0), 0);

  // Per-carrier rollup
  const byCarrier = new Map<string, { sys: string; carrier: string; net: number; rows: number }>();
  for (const r of rows) {
    const key = `${r.sys}|${r.carrier}`;
    if (!byCarrier.has(key)) {
      byCarrier.set(key, { sys: r.sys, carrier: r.carrier, net: 0, rows: 0 });
    }
    const cell = byCarrier.get(key)!;
    cell.net += r.net_amount || 0;
    cell.rows++;
  }
  const topCarriers = [...byCarrier.values()]
    .sort((a, b) => b.net - a.net)
    .slice(0, 5);

  const recentPayments = payments
    .slice()
    .sort((a, b) => (b.paid_at || '').localeCompare(a.paid_at || ''))
    .slice(0, 5);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
          {greeting}, {displayName}
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Your ACA commission earnings at a glance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric
          label="Lifetime paid"
          value={fmt.money(lifetimePaid)}
          icon={<DollarSign size={14} />}
          accent
        />
        <Metric
          label="Pending"
          value={fmt.money(pending)}
          icon={<Clock size={14} />}
          warning={pending > 0}
        />
        <Metric
          label={`Paid in ${new Date().getFullYear()}`}
          value={fmt.money(thisYearPaid)}
          icon={<Calendar size={14} />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recent payments */}
        <div className="card p-0 overflow-hidden">
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
              <HistoryIcon size={14} className="inline mr-2" />
              Recent payments
            </h3>
            {payments.length > 0 && (
              <Link
                to="/my/payments"
                className="text-xs"
                style={{ color: 'var(--color-primary)' }}
              >
                See all →
              </Link>
            )}
          </div>
          {recentPayments.length === 0 ? (
            <p className="p-6 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
              No payments yet — your first payment will show up here.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentPayments.map((p) => (
                  <tr
                    key={p.payment_id}
                    onClick={() => navigate(`/my/payments/${p.payment_id}`)}
                    style={{ cursor: 'pointer' }}
                    title="Click to view payment detail"
                  >
                    <td className="text-xs">{fmt.dateOnly(p.paid_at)}</td>
                    <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {p.period_label || p.note || p.sys}
                    </td>
                    <td className="text-right font-mono font-semibold">
                      {fmt.money(p.statement_net ?? p.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top carriers */}
        <div className="card p-0 overflow-hidden">
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
              <TrendingUp size={14} className="inline mr-2" />
              Top carriers
            </h3>
            {byCarrier.size > 0 && (
              <Link
                to="/my/carriers"
                className="text-xs"
                style={{ color: 'var(--color-primary)' }}
              >
                See all →
              </Link>
            )}
          </div>
          {topCarriers.length === 0 ? (
            <p className="p-6 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
              No commissions attributed to you yet.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Carrier</th>
                  <th className="text-right">Policies</th>
                  <th className="text-right">Net total</th>
                </tr>
              </thead>
              <tbody>
                {topCarriers.map((c) => (
                  <tr key={`${c.sys}-${c.carrier}`}>
                    <td>
                      <Link
                        to={`/my/carriers/${encodeURIComponent(c.sys)}/${encodeURIComponent(c.carrier)}`}
                        className="flex items-center gap-2"
                        style={{ color: 'var(--color-text)' }}
                      >
                        <Building2 size={12} style={{ color: 'var(--color-primary)' }} />
                        <span>
                          {c.carrier}
                          <span
                            className="text-xs ml-1"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {c.sys}
                          </span>
                        </span>
                      </Link>
                    </td>
                    <td className="text-right font-mono">{c.rows}</td>
                    <td className="text-right font-mono font-semibold">
                      {fmt.money(c.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="text-xs italic text-center" style={{ color: 'var(--color-text-muted)' }}>
        Don't see something you expect? Contact your admin — it may not have been mapped to your
        agent record yet, or it might be in a not-yet-imported batch.
      </p>
    </div>
  );
}

function Metric({
  label,
  value,
  icon,
  accent,
  warning,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  accent?: boolean;
  warning?: boolean;
}) {
  const cls = accent
    ? 'metric-card metric-card-success'
    : warning
      ? 'metric-card metric-card-warning'
      : 'metric-card metric-card-primary';
  return (
    <div className={cls}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <p
          className="text-xs uppercase tracking-wider"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {label}
        </p>
      </div>
      <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
        {value}
      </p>
    </div>
  );
}
